import { useCallback, useMemo, useState } from 'react';
import {
  COVER_REDO_KINDS,
  coverRedoCredits,
  coverRedoItemsToMake,
  type CoverRedoKind,
  type CoverRedoState,
} from 'fym-shared/src/music24/coverRedo';
import { usePreviewCoverRedoMutation, useStartCoverRedoMutation } from '../../../../../../redux/api/fymAPI';
import { useVideoCreditGate } from '../../../credits/useVideoCreditGate';
import { coverRedoError, type CoverRedoError } from './coverRedoErrors';

/** The groups of a state that have a file to make, in the order of the sheet. Pure. */
export function kindsToMake(state: CoverRedoState): CoverRedoKind[] {
  return COVER_REDO_KINDS.filter((kind) => state.counts[kind] > 0);
}

/**
 * The confirm dialog of the cover redo (design kit-redo-cover b). Open reads a fresh dry run (it
 * charges nothing). Every group with a file starts ticked; a tick changes the count and the cost on
 * the client (`coverRedoCredits`), with no new request. Confirm sends the ticked groups, the post
 * switch and the cost on screen as `expectedCredits`. A changed cost (409 `M24_COVER_REDO_CONFIRM`)
 * reads the dry run again and shows the new cost; a 402 for video credits closes the dialog (the
 * credit packs open from `customBaseQuery`). A cost above the credits left turns the button into
 * "Buy credits".
 */
export function useCoverRedoDialog(input: { accountId: number; catalogItemUuid: string }) {
  const { accountId, catalogItemUuid } = input;
  const [previewRedo, previewState] = usePreviewCoverRedoMutation();
  const [startRedo, startState] = useStartCoverRedoMutation();
  const { offerCredits } = useVideoCreditGate(accountId);
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<CoverRedoState>();
  const [ticked, setTicked] = useState<CoverRedoKind[]>([]);
  const [swapPosts, setSwapPosts] = useState(true);
  const [error, setError] = useState<CoverRedoError>();

  const readState = useCallback(
    async (keepTicks: boolean) => {
      const answer = await previewRedo({ accountId, catalogItemUuid, swapPosts: true, dryRun: true }).unwrap();
      setState(answer);
      setTicked((current) => {
        const all = kindsToMake(answer);
        return keepTicks ? all.filter((kind) => current.includes(kind)) : all;
      });
      return answer;
    },
    [accountId, catalogItemUuid, previewRedo]
  );

  const open = useCallback(() => {
    setIsOpen(true);
    setState(undefined);
    setError(undefined);
    setSwapPosts(true);
    readState(false).catch((reason: unknown) => {
      console.error('Failed to read the cover redo', reason);
      setError({ kind: 'other' });
    });
  }, [readState]);

  const close = useCallback(() => {
    if (!startState.isLoading) {
      setIsOpen(false);
    }
  }, [startState.isLoading]);

  const toggleKind = useCallback((kind: CoverRedoKind) => {
    setError(undefined);
    setTicked((current) => (current.includes(kind) ? current.filter((one) => one !== kind) : [...current, kind]));
  }, []);

  const toggleSwap = useCallback(() => setSwapPosts((current) => !current), []);

  const items = useMemo(() => state?.items ?? [], [state]);
  const credits = useMemo(() => coverRedoCredits(items, ticked), [items, ticked]);
  const count = useMemo(() => coverRedoItemsToMake(items, ticked).length, [items, ticked]);
  const left = state?.creditsAvailable ?? null;
  const isShort = left !== null && credits > left;

  const confirm = useCallback(() => {
    if (state === undefined || count === 0) {
      return;
    }
    if (isShort) {
      offerCredits();
      return;
    }
    setError(undefined);
    startRedo({ accountId, catalogItemUuid, kinds: ticked, swapPosts, expectedCredits: credits })
      .unwrap()
      .then(() => setIsOpen(false))
      .catch((reason: unknown) => {
        const refusal = coverRedoError(reason);
        if (refusal.kind === 'videoCredits') {
          setIsOpen(false);
          return;
        }
        if (refusal.kind === 'costChanged') {
          // The list changed while the dialog was open: read it again with the same ticks.
          readState(true).catch(console.error);
        } else if (refusal.kind === 'other') {
          console.error('Failed to redo the kit with the new cover', reason);
        }
        setError(refusal);
      });
  }, [
    accountId,
    catalogItemUuid,
    count,
    credits,
    isShort,
    offerCredits,
    readState,
    startRedo,
    state,
    swapPosts,
    ticked,
  ]);

  return {
    isOpen,
    state,
    isLoading: previewState.isLoading && state === undefined,
    isStarting: startState.isLoading,
    ticked,
    swapPosts,
    credits,
    count,
    left,
    isShort,
    error,
    open,
    close,
    toggleKind,
    toggleSwap,
    confirm,
  };
}

export type CoverRedoDialogModel = ReturnType<typeof useCoverRedoDialog>;
